import React, { useMemo, useState } from "react";
import ReviewCard from "./ReviewCard";
import {
  EBAY_FEEDBACK_SOURCE_URL,
  EBAY_POSITIVE_COUNT,
  getEbayReviewsForWall,
  getIngestedEbayReviewCount,
  ebayReviewToMarketplaceItem,
} from "../../lib/ebayReviews";

const BATCH = 10;

/**
 * Progressive disclosure for the full positive eBay feedback list.
 * Substantive quotes first; automated delivery notes later.
 */
export default function EbayReviewsExpandableList({ className = "" }) {
  const wall = useMemo(() => getEbayReviewsForWall(), []);
  const items = useMemo(() => wall.map(ebayReviewToMarketplaceItem), [wall]);
  const [visible, setVisible] = useState(Math.min(BATCH, items.length));
  const ingested = getIngestedEbayReviewCount();

  const remaining = Math.max(0, items.length - visible);
  const nextBatch = Math.min(BATCH, remaining);

  return (
    <section className={className} aria-labelledby="ebay-wall-heading">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h2
            id="ebay-wall-heading"
            className="font-sans font-semibold text-2xl md:text-[1.65rem] text-ivory m-0"
          >
            All positive feedback
          </h2>
          <p className="text-sm text-sage max-w-2xl mt-2 mb-0 leading-relaxed">
            Showing buyer quotes first. Delivery-only automated notes are included further down so the page stays readable.
          </p>
        </div>
        <p className="text-[11px] text-sage/75 m-0 tabular-nums">
          {Math.min(visible, items.length)} of {ingested} on site
          <span className="text-sage/50"> · </span>
          {EBAY_POSITIVE_COUNT} positive on eBay
        </p>
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gold/30 bg-panel/80 p-8 text-center">
          <p className="text-sm text-sage m-0">No eBay feedback loaded yet.</p>
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.slice(0, visible).map((item) => (
              <ReviewCard key={item.id} item={item} tone="dark" expandable />
            ))}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            {remaining > 0 ? (
              <button
                type="button"
                onClick={() => setVisible((v) => Math.min(items.length, v + BATCH))}
                className="inline-flex min-h-[44px] items-center justify-center rounded-full border border-gold/40 bg-gold/10 px-6 py-2.5 text-sm text-gold hover:bg-gold/20 transition"
              >
                Show more reviews
                <span className="ml-2 text-gold/70 tabular-nums">(+{nextBatch})</span>
              </button>
            ) : null}
            <a
              href={EBAY_FEEDBACK_SOURCE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-[44px] items-center justify-center rounded-full border border-white/16 px-6 py-2.5 text-sm text-ivory/90 hover:border-gold/35 hover:text-gold transition"
            >
              See all feedback on eBay
            </a>
          </div>
        </>
      )}
    </section>
  );
}
